/**
 * MenuSearchModal - Extracted from App.js Phase 4
 * Restaurant menu search with chain detection and smart suggestions
 */
import React from 'react';

const MenuSearchModal = ({
  isOpen,
  onClose,
  analyzing,
  menuSearchQuery,
  handleRestaurantQueryChange,
  menuSearchCity,
  setMenuSearchCity,
  searchRestaurantMenu,
  isKnownChain,
  menuSearchResults,
  selectedMenuItem,
  setSelectedMenuItem,
  addMenuItemToMeals,
  showSmartSuggestions,
  SMART_SUGGESTIONS,
  getCuisineType
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
        <h2 className="heading-3">Search Restaurant Menu</h2>
        <p className="modal-description" style={{ marginBottom: '4px' }}>
          Best-fit guidance based on your targets -- not exact tracking.
        </p>
        
        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
              Restaurant & Menu Item
            </label>
            <input type="text" className="search-input" placeholder="e.g., McDonald's Big Mac Meal"
              value={menuSearchQuery}
              onChange={(e) => handleRestaurantQueryChange(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchRestaurantMenu()}
              style={{ width: '100%', padding: '12px', borderRadius: '8px' }} />
            
            {menuSearchQuery.length >= 3 && (
              <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: isKnownChain ? '#4CAF50' : 'var(--text-muted)' }}>
                {isKnownChain 
                  ? 'National/global chain detected -- using brand nutrition data' 
                  : 'Add city below to improve accuracy for local restaurants'}
              </p>
            )}
          </div>
          
          {!isKnownChain && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
                City <span style={{ color: 'var(--text-muted)', fontWeight: '400' }}>(optional for chains)</span>
              </label>
              <input type="text" className="search-input" placeholder="e.g., Vancouver, BC"
                value={menuSearchCity}
                onChange={(e) => setMenuSearchCity(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchRestaurantMenu()}
                style={{ width: '100%', padding: '12px', borderRadius: '8px' }} />
            </div>
          )}
          
          <button className="btn-primary full-width" onClick={searchRestaurantMenu}
            disabled={analyzing || !menuSearchQuery.trim()} style={{ marginTop: '8px' }}>
            {analyzing ? 'Searching...' : 'Search Menu'}
          </button>
        </div>

        {menuSearchResults && menuSearchResults.menu_items && menuSearchResults.menu_items.length > 0 && (
          <div className="search-results" style={{ background: 'rgba(76, 175, 80, 0.05)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>{menuSearchResults.restaurant_name}</h3>
              <span style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '12px',
                background: menuSearchResults.match_type === 'CHAIN' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 193, 7, 0.2)',
                color: menuSearchResults.match_type === 'CHAIN' ? '#4CAF50' : '#FFC107' }}>
                {menuSearchResults.source_label}
              </span>
            </div>
            
            <p style={{ margin: '0 0 12px 0', fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              {menuSearchResults.match_type === 'CHAIN' 
                ? 'National/global chain detected -- using brand nutrition data'
                : 'Estimated based on similar menu items and portion sizes.'}
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
              {menuSearchResults.menu_items.map((item) => (
                <div key={item.item_id}
                  onClick={() => setSelectedMenuItem(selectedMenuItem?.item_id === item.item_id ? null : item)}
                  style={{
                    padding: '12px',
                    background: selectedMenuItem?.item_id === item.item_id ? 'rgba(102, 126, 234, 0.15)' : 'rgba(255,255,255,0.05)',
                    borderRadius: '8px', cursor: 'pointer',
                    border: selectedMenuItem?.item_id === item.item_id ? '1px solid rgba(102, 126, 234, 0.5)' : '1px solid transparent',
                    transition: 'all 0.2s'
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '500', fontSize: '14px' }}>{item.name}</span>
                    <span style={{ color: '#4CAF50', fontWeight: '600', fontSize: '14px' }}>{item.calories} kcal</span>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    <span>P: {item.protein_g}g</span>
                    <span>C: {item.carbs_g}g</span>
                    <span>F: {item.fat_g}g</span>
                  </div>
                  {item.serving_size && (
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Serving: {item.serving_size}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {selectedMenuItem && (
              <button className="btn-primary full-width" onClick={() => addMenuItemToMeals({
                food_items: `${menuSearchResults.restaurant_name} - ${selectedMenuItem.name}`,
                calories: selectedMenuItem.calories,
                protein: selectedMenuItem.protein_g,
                carbs: selectedMenuItem.carbs_g,
                fat: selectedMenuItem.fat_g,
                fiber: selectedMenuItem.fiber_g || 0,
                sodium: selectedMenuItem.sodium_mg || 0,
                is_chain: menuSearchResults.match_type === 'CHAIN'
              })} style={{ marginTop: '12px' }}>
                Add {selectedMenuItem.name} to My Meals
              </button>
            )}
          </div>
        )}

        {showSmartSuggestions && (
          <div style={{ background: 'rgba(102, 126, 234, 0.08)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid rgba(102, 126, 234, 0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px' }}>Smart Suggestions</span>
              <div>
                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  MIQ couldn't find exact nutrition data, but here are smart choices that fit your targets.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {SMART_SUGGESTIONS[getCuisineType(menuSearchQuery)]?.map((suggestion, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                  <span style={{ fontSize: '18px' }}>{suggestion.icon}</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '500' }}>{suggestion.tip}</p>
                    <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#4CAF50' }}>{suggestion.impact}</p>
                  </div>
                </div>
              ))}
            </div>
            <p style={{ margin: '12px 0 0 0', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center' }}>
              Use these tips to make smarter choices at any restaurant.
            </p>
          </div>
        )}

        <button className="btn-secondary full-width" onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

export default MenuSearchModal;
